import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import PageIndicator from '@/components/PageIndicator';
import SignatureCanvas from '@/components/SignatureCanvas';
import { generateAndSavePagePDF } from '@/utils/pdfGenerator';
import html2pdf from 'html2pdf.js';
import confetti from 'canvas-confetti';
import { Crown, Clock, MessageCircleMore } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Local image paths to avoid CORS issues with html2canvas PDF generation
const LOGO_URL = '/images/TCA_Logo.png';
const BANNER_URL = '/images/TCA_Banner.png';
const TCA_SHORT_LOGO = '/images/TCA_Short_Logo.png';

// Static package metadata (styling, descriptions - doesn't change)
const packageMetadata = [
  {
    id: 'bronze',
    name: 'BRONZE',
    subtitle: 'The Essentials',
    color: 'from-amber-700 to-amber-900',
    borderColor: 'border-amber-700',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-900',
    bestFor: 'Standard salaried employees with no travel allowance or home office.',
    includes: [
      'Covers all the essentials required to ensure your SARS tax compliance.',
      'Tax compliance certificate',
      '1x telephonic consultation (30min)',
      'Assessment of medical aid certificates',
      'Medical out-of-pocket expenses',
      'Assessment of investment income certificates',
      'Capital gains tax calculations',
      'Submission of income tax return (ITR12 / ITA34)'
    ],
    excludes: ['Travel logbooks', 'Home office claims', 'Depreciation claims', 'Sideline business (side-hustle)']
  },
  {
    id: 'silver',
    name: 'SILVER',
    subtitle: 'The Commuter',
    color: 'from-gray-400 to-gray-600',
    borderColor: 'border-gray-500',
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-700',
    bestFor: 'Employees with a Travel Allowance or Company Car.',
    whyUpgrade: 'If you have a travel allowance, SARS requires a compliant logbook. We differentiate ourselves by removing the headache of the logbook. An incorrect logbook is the #1 reason SARS declines travel claims. We ensure yours is SARS compliant.',
    includes: [
      'Everything in Bronze',
      'Professional logbook preparation (if you have none)',
      'SARS logbook template conversion (we convert yours)',
      'Multiple vehicles allowed and welcome'
    ],
    excludes: ['Home office claims', 'Depreciation claims', 'Commission expenses claims', 'Sideline business (side-hustle)']
  },
  {
    id: 'gold',
    name: 'GOLD',
    subtitle: 'The Remote Pro',
    color: 'from-yellow-500 to-yellow-700',
    borderColor: 'border-yellow-600',
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-800',
    bestFor: 'Employees who work from home and use their own study/home office and equipment to perform their duties.',
    whyUpgrade: 'It is custom for taxpayers to claim Home Office expenses after COVID, but we go deeper. Much deeper. Where others strictly look at electricity and rent, we look at all 9 available qualifying categories.',
    includes: [
      'Everything in Silver',
      'Advanced home office calculations',
      'Floor plan drafting + Title Deed',
      'All 9 qualifying home office expenses categories',
      'Wear-and-tear depreciation schedule for assets (laptop, desk, chair, UPS, fan etc.)'
    ],
    excludes: ['Commission expenses claims', 'Sideline business (side-hustle)']
  },
  {
    id: 'platinum',
    name: 'PLATINUM',
    subtitle: 'The Entrepreneur',
    color: 'from-slate-600 to-slate-800',
    borderColor: 'border-slate-600',
    bgColor: 'bg-slate-50',
    textColor: 'text-slate-800',
    bestFor: 'Commission earners and those with a Sideline Business/Side-Hustle.',
    whyUpgrade: 'platinum-special',
    includes: [
      'Everything in Gold',
      'Sideline business expenses summary and slips schedules',
      'Business loss offset optimization',
      'Commission expenses summary and slips schedules',
      '+1 extra telephonic consultation (30min)'
    ],
    excludes: []
  }
];

// Helper function to format currency with commas
const formatCurrency = (amount) => {
  return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// Helper function to calculate min spend string
const calculateMinSpendString = (initiation, submission, refundPct) => {
  const total = initiation + submission;
  if (refundPct > 0) {
    return `R${formatCurrency(total)} + ${refundPct}%`;
  }
  return `R${formatCurrency(total)}`;
};

// Merge static metadata with dynamic pricing
const mergePackagesWithPricing = (metadata, pricingData) => {
  return metadata.map(pkg => {
    const pricing = pricingData?.packages?.[pkg.id] || {
      initiationFee: 750,
      submissionFee: 750,
      expressFee: 250,
      refundFee: 0
    };
    
    return {
      ...pkg,
      initiationFee: `R${formatCurrency(pricing.initiationFee)}`,
      submissionFee: `R${formatCurrency(pricing.submissionFee)}`,
      refundFee: `${pricing.refundFee}%`,
      expressFee: `+R${formatCurrency(pricing.expressFee)}`,
      minSpend: calculateMinSpendString(pricing.initiationFee, pricing.submissionFee, pricing.refundFee),
      // Store raw values for calculations
      _raw: {
        initiationFee: pricing.initiationFee,
        submissionFee: pricing.submissionFee,
        expressFee: pricing.expressFee,
        refundFee: pricing.refundFee
      }
    };
  });
};

const PageFive = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const clientData = location.state || {};
  
  // Get formId from previous page
  const formId = clientData.formId;
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  // Ref for signature canvas to restore signature before PDF generation
  const signatureCanvasRef = useRef(null);
  
  const [selectedPackage, setSelectedPackage] = useState(clientData.selectedPackage || null);
  const [selectedUrgency, setSelectedUrgency] = useState(clientData.selectedUrgency || 'standard');
  const [signature, setSignature] = useState(clientData.signaturePage5 || null);
  const [signedAt, setSignedAt] = useState(clientData.signedAtPage5 || '');
  const [showLocationWarning, setShowLocationWarning] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [showExpressUpsellModal, setShowExpressUpsellModal] = useState(false);
  const [expressUpsellAcknowledged, setExpressUpsellAcknowledged] = useState(false);
  const [expressSpotsCountdown, setExpressSpotsCountdown] = useState(240);
  
  // Dynamic pricing state
  const [packages, setPackages] = useState([]);
  const [agreementFees, setAgreementFees] = useState(null);
  const [isPricingLoaded, setIsPricingLoaded] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Fetch pricing on mount
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/pricing`);
        const pricingData = await response.json();
        const mergedPackages = mergePackagesWithPricing(packageMetadata, pricingData);
        setPackages(mergedPackages);
        // Store agreement fees for Additional Options section
        setAgreementFees(pricingData.agreementFees || {
          disputeAppealFee: 750,
          taxConsultationFee: 750,
          efilingActivationFee: 550
        });
      } catch (error) {
        console.error('Error fetching pricing:', error);
        // Use default pricing on error
        const mergedPackages = mergePackagesWithPricing(packageMetadata, null);
        setPackages(mergedPackages);
        setAgreementFees({
          disputeAppealFee: 750,
          taxConsultationFee: 750,
          efilingActivationFee: 550
        });
      } finally {
        setIsPricingLoaded(true);
      }
    };
    
    fetchPricing();
  }, [BACKEND_URL]);

  // Format currency with proper formatting (R1,250.00)
  const formatCurrencyDisplay = (amount) => {
    if (amount === undefined || amount === null) return 'R0.00';
    return `R${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Countdown timer for the Express upsell modal's urgency message
  useEffect(() => {
    if (!showExpressUpsellModal) return;
    setExpressSpotsCountdown(240);
    const interval = setInterval(() => {
      setExpressSpotsCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [showExpressUpsellModal]);

  // Update state when returning from Page 4 or Page 6 with preserved data
  useEffect(() => {
    if (clientData.selectedPackage) {
      setSelectedPackage(clientData.selectedPackage);
    }
    if (clientData.selectedUrgency) {
      setSelectedUrgency(clientData.selectedUrgency);
    }
    if (clientData.signaturePage5) {
      setSignature(clientData.signaturePage5);
    }
    if (clientData.signedAtPage5) {
      setSignedAt(clientData.signedAtPage5);
    }
  }, [location.key, clientData.selectedPackage, clientData.selectedUrgency, clientData.signaturePage5, clientData.signedAtPage5]); // Re-run when navigation occurs

  // Get today's date formatted
  const today = new Date();
  const dayOfMonth = today.getDate();
  const monthName = today.toLocaleDateString('en-ZA', { month: 'long' });
  const fullYear = today.getFullYear();

  // Construct full name from client data
  const getFullName = () => {
    const parts = [
      clientData.firstName,
      clientData.secondName,
      clientData.middleName,
      clientData.surname
    ].filter(Boolean);
    return parts.join(' ') || '[Client Full Name]';
  };

  const fullName = getFullName();

  // Validate location - only allow alphabetic letters and spaces
  const validateLocation = (value) => {
    if (!value || value.trim() === '') {
      return '';
    }
    // Check if the value contains only letters and spaces
    const locationRegex = /^[a-zA-Z\s]+$/;
    if (!locationRegex.test(value)) {
      return 'Location can only contain letters (no numbers or special characters)';
    }
    return '';
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setSignedAt(value);
    setLocationError(validateLocation(value));
  };

  // Calculate total min spend based on selection
  const calculateTotalMinSpend = () => {
    if (!selectedPackage || packages.length === 0) return null;
    const pkg = packages.find(p => p.id === selectedPackage);
    if (!pkg || !pkg._raw) return null;
    
    // Use raw values directly
    const { initiationFee, submissionFee, expressFee } = pkg._raw;
    
    // Base total is initiation + submission
    let total = initiationFee + submissionFee;
    
    // Add express fee if selected
    if (selectedUrgency === 'express') {
      total += expressFee;
    }
    
    // Format as currency with commas
    return `R${formatCurrency(total)}`;
  };

  const handleSignatureChange = (signatureData) => {
    setSignature(signatureData);
  };

  const handleClearSignature = () => {
    setSignature(null);
  };

  const isFormValid = () => {
    return selectedPackage && signedAt && signedAt.trim() !== '' && signature && !locationError;
  };

  const handleSubmit = async () => {
    if (!selectedPackage) {
      alert('Please select a service package.');
      return;
    }
    if (!signedAt || signedAt.trim() === '') {
      alert('Please enter the location where you are signing.');
      return;
    }
    // Validate location before proceeding
    const locationValidationError = validateLocation(signedAt);
    if (locationValidationError) {
      alert(locationValidationError);
      return;
    }
    if (!signature) {
      alert('Please provide your signature before proceeding.');
      return;
    }
    
    // Check if location on Page 4 (Tax Services Master Engagement Letter) differs from Page 5 (Tax Return Optimisation & Pricing Guide)
    const page4Location = clientData.signedAtPage4 || '';
    const page5Location = signedAt.trim();
    
    if (page4Location && page5Location && page4Location.toLowerCase() !== page5Location.toLowerCase()) {
      setShowLocationWarning(true);
      return;
    }

    // Upsell nudge: give Standard clients a chance to switch to Express before proceeding
    if (selectedUrgency === 'standard' && !expressUpsellAcknowledged) {
      setShowExpressUpsellModal(true);
      return;
    }

    await proceedToNextPage();
  };

  const handleUpgradeToExpress = () => {
    setSelectedUrgency('express');
    setExpressUpsellAcknowledged(true);
    setShowExpressUpsellModal(false);
    confetti({
      particleCount: 140,
      spread: 80,
      origin: { y: 0.6 },
      colors: ['#f97316', '#fb923c', '#1e3a8a', '#fbbf24']
    });
  };

  const handleStayStandard = () => {
    setExpressUpsellAcknowledged(true);
    setShowExpressUpsellModal(false);
    proceedToNextPage();
  };

  const proceedToNextPage = async () => {
    // Restore signature to canvas before PDF generation (in case it was cleared by zoom/resize)
    if (signatureCanvasRef.current) {
      signatureCanvasRef.current.restoreSignature();
    }
    
    // Generate PDF of current page before navigating
    setIsGeneratingPDF(true);
    
    // Create full-screen overlay to hide PDF generation process from user
    const overlay = document.createElement('div');
    overlay.id = 'pdf-generation-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(255, 255, 255, 0.97);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 99999;
    `;
    overlay.innerHTML = `
      <div style="text-align: center;">
        <div style="width: 48px; height: 48px; border: 4px solid #e5e7eb; border-top-color: #f97316; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
        <p style="font-size: 18px; font-weight: 600; color: #1f2937; margin: 0;">Saving your progress...</p>
        <p style="font-size: 14px; color: #6b7280; margin-top: 8px;">Please wait while we generate your PDF</p>
      </div>
      <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
    `;
    document.body.appendChild(overlay);
    
    const element = document.querySelector('#page-pdf-content');
    if (!element) {
      overlay.remove();
      setIsGeneratingPDF(false);
      alert('Error: Could not find page content.');
      return;
    }
    
    // Store original values for restoration
    const originalStyles = new Map();
    const originalClassNames = new Map();
    
    const hideElement = (el) => {
      if (el) {
        originalStyles.set(el, el.style.cssText);
        el.style.display = 'none';
      }
    };
    
    const modifyStyle = (el, newStyle) => {
      if (el) {
        if (!originalStyles.has(el)) {
          originalStyles.set(el, el.style.cssText);
        }
        Object.assign(el.style, newStyle);
      }
    };
    
    try {
      // === PDF MODIFICATIONS FOR PAGE 5 ===
      // Keep the PDF's original top spacing (the page itself has only 2px above the logo; was py-6 sm:py-8)
      modifyStyle(element, { paddingTop: window.matchMedia('(min-width: 640px)').matches ? '2rem' : '1.5rem' });
      
      // 0. HIDE NON-SELECTED PACKAGES - Only show the selected package card
      const selectedPkg = selectedPackage;
      const packageGrid = element.querySelector('#package-cards-grid');
      if (packageGrid && selectedPkg) {
        element.querySelectorAll('[data-package-id]').forEach(card => {
          if (card.getAttribute('data-package-id') !== selectedPkg) {
            hideElement(card);
          } else {
            modifyStyle(card, { 
              width: '100%', 
              maxWidth: '400px', 
              margin: '0 auto',
              transform: 'none',
              zIndex: 'auto'
            });
            
            // Center the package name badge within the card for PDF
            const packageBadge = card.querySelector('.inline-block.px-6.py-2.rounded-full');
            if (packageBadge) {
              modifyStyle(packageBadge, {
                display: 'block',
                marginLeft: 'auto',
                marginRight: 'auto',
                width: '65%',
                minWidth: '180px',
                paddingLeft: '24px',
                paddingRight: '24px',
                textAlign: 'center'
              });
              
              // Also ensure the text node itself is centered
              if (packageBadge.firstChild && packageBadge.firstChild.nodeType === Node.TEXT_NODE) {
                const textContent = packageBadge.textContent.trim();
                packageBadge.innerHTML = `<span style="display: block; text-align: center; width: 100%;">${textContent}</span>`;
              }
            }
            
            // Ensure checkmark is fully visible and centered in PDF
            const checkmarkContainer = card.querySelector('.absolute.top-2.right-2');
            if (checkmarkContainer) {
              // Move the entire circle down slightly to appear more centered
              modifyStyle(checkmarkContainer, {
                width: '56px',
                height: '56px',
                display: 'flex !important',
                alignItems: 'center',
                justifyContent: 'center',
                top: '12px'
              });
              
              const checkmarkCircle = checkmarkContainer.querySelector('div');
              if (checkmarkCircle) {
                modifyStyle(checkmarkCircle, {
                  width: '56px',
                  height: '56px',
                  fontSize: '34px',
                  display: 'flex !important',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  padding: '0',
                  margin: '0',
                  lineHeight: '56px'
                });
                
                // Center the checkmark using line-height
                const checkmarkText = checkmarkCircle.querySelector('span');
                if (checkmarkText) {
                  modifyStyle(checkmarkText, {
                    display: 'inline-block',
                    lineHeight: '56px',
                    verticalAlign: 'middle',
                    textAlign: 'center',
                    width: '100%',
                    margin: '0',
                    padding: '0',
                    fontSize: '34px'
                  });
                }
              }
            }
          }
        });
        modifyStyle(packageGrid, { 
          display: 'flex', 
          justifyContent: 'center',
          gridTemplateColumns: '1fr'
        });
      }
      
      // 1. Reduce gap between headings
      element.querySelectorAll('h2.text-xl.font-bold.text-center').forEach(el => {
        modifyStyle(el, { marginBottom: '0px', paddingBottom: '0px', marginTop: '0px' });
      });
      element.querySelectorAll('p.text-center.font-semibold, h3.text-center').forEach(el => {
        modifyStyle(el, { marginTop: '0px', paddingTop: '0px', marginBottom: '4px' });
      });
      element.querySelectorAll('.text-2xl, .text-3xl').forEach(el => {
        modifyStyle(el, { marginTop: '0px', marginBottom: '2px' });
      });
      
      // 2. Keep good paragraph spacing
      element.querySelectorAll('p').forEach(el => {
        modifyStyle(el, { marginBottom: '6px', lineHeight: '1.45' });
      });
      
      // 3. Add proper spacing between headings and content
      element.querySelectorAll('h1, h2, h3, h4').forEach(el => {
        modifyStyle(el, { marginTop: '8px', marginBottom: '6px' });
      });
      
      // 4. HIDE the instruction text
      element.querySelectorAll('p').forEach(p => {
        if (p.textContent.includes('Click on a package to select it') || 
            p.textContent.includes('Hover over each card')) {
          hideElement(p);
        }
      });
      
      // 5. Reduce top margin/padding
      modifyStyle(element, { paddingTop: '0px' });
      element.querySelectorAll('.py-6, .sm\\:py-8').forEach(el => {
        modifyStyle(el, { paddingTop: '4px', paddingBottom: '4px' });
      });
      element.querySelectorAll('.flex-1.flex.flex-col').forEach(el => {
        modifyStyle(el, { paddingTop: '0px' });
      });
      
      // 5a. Gap between "CHOOSE YOUR SERVICE PACKAGE" and the card - increased by 20px total
      element.querySelectorAll('h3').forEach(h3 => {
        if (h3.textContent.includes('CHOOSE YOUR SERVICE PACKAGE')) {
          modifyStyle(h3, { marginBottom: '32px' });
        }
      });
      
      // 5b. Spacing around SELECT URGENCY LEVEL - increased gap to radio buttons by 20px total
      element.querySelectorAll('h3').forEach(h3 => {
        if (h3.textContent.includes('SELECT URGENCY LEVEL')) {
          modifyStyle(h3, { marginTop: '-12px', marginBottom: '34px' });
        }
      });
      if (packageGrid) {
        modifyStyle(packageGrid, { marginBottom: '-12px' });
      }
      
      // 5c. Adjust spacing for ADDITIONAL OPTIONS & FEES section
      element.querySelectorAll('h3').forEach(h3 => {
        if (h3.textContent.includes('ADDITIONAL OPTIONS') || h3.textContent.includes('ADDITIONAL FEES')) {
          modifyStyle(h3, { marginTop: '4px', marginBottom: '12px' }); // Increased bottom margin from 4px to 12px (8px increase)
        }
      });
      
      // 5d. Add spacing between ADDITIONAL OPTIONS clauses
      element.querySelectorAll('.additional-option-clause').forEach(clause => {
        modifyStyle(clause, { marginBottom: '2px' });
      });
      
      // Reduce spacing between TAX CONSULTATIONS and CLIENT SELECTION by 30%
      element.querySelectorAll('h3').forEach(h3 => {
        if (h3.textContent.includes('CLIENT SELECTION')) {
          modifyStyle(h3, { marginTop: '-8px', marginBottom: '16px' }); // Increased from 11px to 16px (5px increase)
        }
      });
      
      // Center align green gradient box contents with reduced padding
      element.querySelectorAll('.bg-gradient-to-r').forEach(box => {
        modifyStyle(box, { 
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          paddingTop: '6px',
          paddingBottom: '6px'
        });
      });
      element.querySelectorAll('[class*="from-green"], [class*="from-emerald"]').forEach(box => {
        modifyStyle(box, { 
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          paddingTop: '6px',
          paddingBottom: '6px'
        });
      });
      
      // 5e. Spacing between "Accepted by client..." and green box - reduced by 20%
      element.querySelectorAll('h3').forEach(h3 => {
        if (h3.textContent.includes('Accepted by client by virtue of signature')) {
          modifyStyle(h3, { marginBottom: '13px' }); // Increased from 8px to 13px (5px increase)
        }
      });
      
      // 6. Reduce card padding
      element.querySelectorAll('.p-4.sm\\:p-8').forEach(el => {
        modifyStyle(el, { padding: '12px 16px' });
      });
      
      // 7. Signature canvas - LEFT ALIGNED
      const signatureWrappers = element.querySelectorAll('.signature-wrapper-pdf');
      signatureWrappers.forEach(wrapper => {
        wrapper.setAttribute('style', 'width: 385px !important; max-width: 385px !important; margin-left: 0 !important; margin-right: auto !important; display: block !important;');
      });
      
      element.querySelectorAll('.border-2.border-dashed').forEach(el => {
        el.setAttribute('style', el.getAttribute('style') + '; height: 88px !important; min-height: 88px !important; max-height: 88px !important; width: 385px !important; max-width: 385px !important;');
      });
      
      element.querySelectorAll('canvas').forEach(el => {
        el.setAttribute('style', el.getAttribute('style') + '; height: 84px !important; max-height: 84px !important; width: 385px !important; max-width: 385px !important;');
      });
      
      element.querySelectorAll('.space-y-3').forEach(el => {
        if (el.querySelector('canvas') || el.querySelector('.border-dashed')) {
          el.setAttribute('style', 'width: 385px !important; max-width: 385px !important; margin-left: 0 !important; margin-right: auto !important;');
        }
      });
      
      // 8. Fix Meyerton blue field
      element.querySelectorAll('.bg-blue-50, .text-tca-blue').forEach(el => {
        modifyStyle(el, { 
          display: 'inline-block',
          padding: '2px 8px',
          whiteSpace: 'nowrap',
          overflow: 'visible'
        });
      });
      
      // Clean up any existing temp spans
      element.querySelectorAll('[id^="temp-input-replacement-"]').forEach(span => span.remove());
      element.querySelectorAll('input[type="text"]').forEach(input => {
        if (input.style.display === 'none') {
          input.style.display = '';
        }
      });
      
      // Create replacement spans for input fields
      element.querySelectorAll('input[type="text"]').forEach(input => {
        const value = input.value || input.placeholder || '';
        if (value) {
          const span = document.createElement('span');
          span.textContent = value;
          span.style.cssText = 'font-weight: bold; color: #1e40af; background-color: #eff6ff; padding: 2px 8px; border-radius: 4px; display: inline-block;';
          span.id = 'temp-input-replacement-' + Math.random().toString(36).substr(2, 9);
          input.style.display = 'none';
          input.parentNode.insertBefore(span, input.nextSibling);
        }
      });
      
      // 9. Reduce list item spacing
      element.querySelectorAll('ul').forEach(ul => {
        modifyStyle(ul, { marginTop: '6px', marginBottom: '6px' });
      });
      element.querySelectorAll('li').forEach(li => {
        modifyStyle(li, { marginBottom: '4px', lineHeight: '1.5' });
      });
      
      // 10. Reduce spacing from the last section
      element.querySelectorAll('.border-t.pt-6').forEach(el => {
        modifyStyle(el, { paddingTop: '8px', paddingBottom: '0px', marginBottom: '0px' });
      });
      
      element.querySelectorAll('.bg-gray-50.p-4.rounded-lg').forEach((el, index, arr) => {
        if (index === arr.length - 1) {
          modifyStyle(el, { padding: '8px 12px', marginBottom: '0px', marginTop: '8px' });
        }
      });
      
      modifyStyle(element, { paddingBottom: '0px', marginBottom: '0px' });
      
      // Hide elements marked for PDF exclusion
      element.querySelectorAll('[data-hide-in-pdf="true"]').forEach(el => hideElement(el));
      
      // Add PDF footer
      const footer = document.createElement('div');
      footer.id = 'temp-pdf-footer';
      footer.style.cssText = `
        width: 100%;
        padding: 16px 0 8px 0;
        margin-top: 20px;
        border-top: 1px solid #e5e7eb;
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
      `;
      footer.innerHTML = `
        <div style="font-size: 14px; color: #374151; font-weight: 500; margin-bottom: 8px;">
          Tax Return Optimisation & Pricing Guide
        </div>
        <div style="font-size: 13px; color: #6b7280;">
          Document <strong style="color: #111827;">05</strong> of <strong style="color: #111827;">07</strong>
        </div>
        <img src="/images/TCA_Short_Logo.png" alt="TCA Logo" style="position: absolute; right: -6px; top: 50%; transform: translateY(-50%); height: 48px; width: auto; opacity: 0.8;" />
      `;
      element.appendChild(footer);
      
      // Generate PDF
      const pdfOptions = {
        margin: [4, 6, 4, 6],
        filename: `Page05_${formId || 'form'}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { 
          scale: 2, useCORS: true, logging: false, letterRendering: true,
          scrollX: 0, scrollY: 0, windowWidth: element.scrollWidth
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };
      
      const pdfBlob = await html2pdf().set(pdfOptions).from(element).outputPdf('blob');
      
      // === RESTORE ORIGINAL STATE ===
      const tempFooter = document.getElementById('temp-pdf-footer');
      if (tempFooter) tempFooter.remove();
      
      element.querySelectorAll('[id^="temp-input-replacement-"]').forEach(span => span.remove());
      
      element.querySelectorAll('input[type="text"]').forEach(input => {
        if (input.style.display === 'none') {
          input.style.display = '';
        }
      });
      
      originalClassNames.forEach((origClassName, el) => { el.className = origClassName; });
      originalStyles.forEach((origStyle, el) => { el.style.cssText = origStyle; });
      
      // Save PDF to backend with retry logic and verification
      const base64data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(pdfBlob);
      });
      
      const MAX_RETRIES = 3;
      let saveSuccessful = false;
      
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          console.log(`[Page5] Save attempt ${attempt}/${MAX_RETRIES}...`);
          
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/save-page`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              form_id: formId,
              page_number: 5,
              pdf_data: base64data,
              page_title: 'Tax Return Optimisation & Pricing Guide',
              client_data: clientData
            })
          });
          
          if (!response.ok) {
            throw new Error(`Server returned ${response.status}`);
          }
          
          // Verify the PDF was saved
          const verifyResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/debug/${formId}`);
          if (verifyResponse.ok) {
            const verifyData = await verifyResponse.json();
            const page5Saved = verifyData.pages?.some(p => p.page_number === 5);
            if (page5Saved) {
              console.log('[Page5] PDF saved and verified successfully');
              saveSuccessful = true;
              break;
            } else {
              throw new Error('PDF not found after save');
            }
          }
        } catch (err) {
          console.error(`[Page5] Save attempt ${attempt} failed:`, err.message);
          if (attempt < MAX_RETRIES) {
            await new Promise(r => setTimeout(r, 2000));
          }
        }
      }
      
      if (!saveSuccessful) {
        overlay.remove();
        setIsGeneratingPDF(false);
        alert('Failed to save your progress after multiple attempts. Please check your internet connection and try again.');
        return;
      }
      
      // Remove overlay
      overlay.remove();
      setIsGeneratingPDF(false);
      
      // Navigate to Page 6
      const selectedPkgInfo = packages.find(p => p.id === selectedPackage);
      navigate('/page-6', { 
        state: { 
          ...clientData,
          selectedPackage,
          selectedUrgency,
          signedAtPage5: signedAt,
          signaturePage5: signature,
          selectedPackageName: selectedPkgInfo?.name || '',
          selectedPackageInitiationFee: selectedPkgInfo?.initiationFee || '',
          selectedPackageSubmissionFee: selectedPkgInfo?.submissionFee || '',
          selectedPackageExpressFee: selectedPkgInfo?.expressFee || '',
          selectedTotalMinSpend: calculateTotalMinSpend() || ''
        } 
      });
      
    } catch (error) {
      console.error('[Page5] PDF generation error:', error);
      const overlayEl = document.getElementById('pdf-generation-overlay');
      if (overlayEl) overlayEl.remove();
      setIsGeneratingPDF(false);
      alert('An error occurred while generating your PDF. Please try again.');
    }
  };

  const handleBack = () => {
    navigate('/page-4', { 
      state: {
        ...clientData,
        selectedPackage,
        selectedUrgency,
        signedAtPage5: signedAt,
        signaturePage5: signature
      }
    });
  };

  // Show loading state while fetching pricing
  if (!isPricingLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-tca-blue rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading pricing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Main Content - wrapped for PDF capture */}
      <main id="page-pdf-content" className="flex-1 flex flex-col items-center px-4 pt-[2px] pb-6 sm:pb-8 bg-white">
        {/* Logo Section */}
        <div className="mb-4 sm:mb-6 flex flex-col items-center gap-3">
          <img 
            src={LOGO_URL} 
            alt="TCA Auditors Logo" 
            className="h-16 sm:h-20 w-auto object-contain"
          />
          <img 
            src={BANNER_URL} 
            alt="Tax & Auditing Consultants" 
            className="h-10 sm:h-14 w-auto object-contain"
          />
        </div>

        {/* Main Heading */}
        <div className="mb-6 pb-2">
          <h1 className="text-xl sm:text-2xl font-extrabold text-tca-blue text-center tracking-wide inline-block underline-tca-blue">
            TAX SERVICES AGREEMENT
          </h1>
        </div>

        {/* Pricing Guide Document */}
        <Card className="w-full max-w-5xl border shadow-card">
          <CardContent className="p-4 sm:p-8">
            {/* Document Header */}
            <div className="text-center mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 underline">TAX RETURN OPTIMISATION & PRICING GUIDE</h2>
            </div>

            {/* Introduction Section */}
            <div className="space-y-4 mb-8 text-sm sm:text-base text-gray-800 leading-relaxed">
              <h3 className="font-bold text-lg underline">INTRODUCTION: MAXIMISE YOUR REFUND</h3>
              
              <p>
                Thank you for the opportunity to be of service. At TC Auditors, we don't just "submit returns"; we engineer tax efficiency. 
                Based on our historical performance, we often manage to double or even triple a client's refund compared to when they file personally. 
                By leveraging specific legislative allowances—such as Home Office calculations, Depreciation schedules, and Sideline Business losses—we 
                aim to legally minimize your tax burden and maximize your cash back.
              </p>
              
              <p>
                <strong>Why choose a higher tier?</strong> While our Bronze package covers the basics, our Silver, Gold, and Platinum packages are designed 
                to unlock specific tax deductions that the average taxpayer overlooks. If you travel for work, work from home, or have a side hustle, 
                upgrading your package is an investment that usually pays for itself many times over in the form of a larger refund.
              </p>
            </div>

            {/* Package Selection Section */}
            <div className="mb-8">
              <h3 className="font-bold text-lg underline mb-6">CHOOSE YOUR SERVICE PACKAGE</h3>
              <p className="text-sm text-gray-600 mb-4">Click on a package to select it. Hover over each card to see more details.</p>
              
              {/* Package Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" id="package-cards-grid">
                {packages.map((pkg) => (
                  <div
                    key={pkg.id}
                    data-package-id={pkg.id}
                    data-testid={`package-card-${pkg.id}`}
                    onClick={() => setSelectedPackage(pkg.id)}
                    className={`
                      relative cursor-pointer rounded-xl overflow-visible transition-all duration-300 transform
                      hover:scale-105 hover:z-10 hover:shadow-2xl
                      ${selectedPackage === pkg.id ? 'scale-110 z-20 shadow-2xl' : ''}
                      ${pkg.bgColor}
                    `}
                  >
                    {/* Animated Border for Selected Package */}
                    {selectedPackage === pkg.id && (
                      <div className={`absolute inset-[-3px] rounded-xl ${pkg.borderColor} border-4 animate-border-pulse`} style={{ color: 'inherit' }}></div>
                    )}
                    
                    <div className={`relative p-1.5 border-2 rounded-xl ${selectedPackage === pkg.id ? pkg.borderColor + ' border-4' : 'border-gray-200'} ${pkg.bgColor}`}>
                      {/* Package Header */}
                      <div className={`text-center mb-3 pb-3 border-b ${pkg.borderColor}`}>
                        <div className={`inline-block px-6 py-2 rounded-full bg-gradient-to-r ${pkg.color} text-white font-bold text-base mb-2 mx-auto`}>
                          {pkg.name}
                        </div>
                        <p className={`text-xs ${pkg.textColor} font-semibold`}>{pkg.subtitle}</p>
                      </div>
                      
                      {/* Best For */}
                      <p className="text-xs text-gray-600 mb-1 italic">
                        <strong>Best for:</strong> {pkg.bestFor}
                      </p>
                      
                      {/* Why Upgrade - only for packages that have it */}
                      {pkg.whyUpgrade && pkg.whyUpgrade !== 'platinum-special' && (
                        <p className="text-xs text-gray-600 mb-3 italic text-justify">
                          <strong>Why upgrade?</strong> {pkg.whyUpgrade}
                        </p>
                      )}
                      
                      {/* Platinum Special Why Upgrade with bolded words */}
                      {pkg.whyUpgrade === 'platinum-special' && (
                        <p className="text-xs text-gray-600 mb-3 italic text-justify">
                          <strong>Why upgrade?</strong> This is our premier service designed for sole proprietors and sideline entrepreneurs. While standard returns focus on income, this package focuses on tax efficiency. Turn your business costs into a tax advantage. We analyze your business costs as opportunities to offset your personal tax liability. This strategic approach maximizes your <strong>refund potential</strong>—in significant cases, potentially recovering a substantial portion of your PAYE.
                        </p>
                      )}
                      
                      {/* Spacer for packages without whyUpgrade */}
                      {!pkg.whyUpgrade && <div className="mb-2"></div>}
                      
                      {/* Includes */}
                      <div className="mb-3">
                        <p className="text-xs font-bold text-gray-700 mb-1">Includes:</p>
                        <ul className="text-xs space-y-1">
                          {pkg.includes.map((item, idx) => {
                            // Check if item references another package (e.g., "Everything in Bronze")
                            const packageRef = item.match(/^Everything in (Bronze|Silver|Gold)$/i);
                            if (packageRef) {
                              const refPkgName = packageRef[1].toUpperCase();
                              const refPkg = packages.find(p => p.name === refPkgName);
                              return (
                                <li key={idx} className="flex items-center gap-1">
                                  <span className="text-green-600">✓</span>
                                  <span className="text-gray-700">Everything in </span>
                                  <span className={`inline-block px-2 py-0.5 rounded-full bg-gradient-to-r ${refPkg?.color || 'from-gray-400 to-gray-600'} text-white font-bold text-xs`}>
                                    {refPkgName}
                                  </span>
                                </li>
                              );
                            }
                            // Check if item has parenthetical text to italicize
                            const match = item.match(/^(.+?)(\s*\([^)]+\))$/);
                            return (
                              <li key={idx} className="flex items-start gap-1">
                                <span className="text-green-600 mt-0.5">✓</span>
                                <span className="text-gray-700">
                                  {match ? (
                                    <>{match[1]}<em className="text-gray-500">{match[2]}</em></>
                                  ) : item}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                      
                      {/* Excludes */}
                      {pkg.excludes.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-bold text-gray-700 mb-1">Excludes:</p>
                          <ul className="text-xs space-y-1">
                            {pkg.excludes.map((item, idx) => (
                              <li key={idx} className="flex items-start gap-1">
                                <span className="text-red-500 mt-0.5">✗</span>
                                <span className="text-gray-500">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Pricing */}
                      <div className={`mt-3 pt-3 border-t ${pkg.borderColor}`}>
                        <p className="text-xs font-bold text-gray-700 mb-2">Pricing:</p>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span>Initiation Fee: <em className="text-gray-500">(due now)</em></span>
                            <span className="font-bold">{pkg.initiationFee}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Submission Fee: <em className="text-gray-500">(due later)</em></span>
                            <span className="font-bold">{pkg.submissionFee}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Refund Fee: <em className="text-gray-500">(due upon refund)</em></span>
                            <span className="font-bold">{pkg.refundFee}</span>
                          </div>
                          <div className="flex justify-between text-gray-500">
                            <span>Express Fee: <em className="text-gray-500">(due now)</em></span>
                            <span>{pkg.expressFee}</span>
                          </div>
                        </div>
                        <div className={`mt-2 pt-2 border-t ${pkg.borderColor} text-center`}>
                          <span className="text-xs text-gray-600">Min. Spend: </span>
                          <span className={`font-bold ${pkg.textColor}`}>{pkg.minSpend}</span>
                        </div>
                      </div>
                      
                      {/* Selected Indicator */}
                      {selectedPackage === pkg.id && (
                        <div className="absolute top-2 right-2">
                          <div className={`w-14 h-14 rounded-full bg-gradient-to-r ${pkg.color} flex items-center justify-center shadow-lg`}>
                            <span className="text-white text-3xl font-bold leading-none">✓</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Urgency Selection */}
            <div className="mb-8 mt-12">
              <h3 className="font-bold text-lg underline mb-4">SELECT URGENCY LEVEL</h3>
              <div className="flex flex-wrap gap-4">
                <label className={`flex items-center gap-2 p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedUrgency === 'standard' ? 'border-tca-blue bg-blue-50' : 'border-gray-200 hover:border-gray-400'}`}>
                  <input 
                    type="radio" 
                    name="urgency" 
                    value="standard" 
                    checked={selectedUrgency === 'standard'}
                    onChange={(e) => setSelectedUrgency(e.target.value)}
                    className="w-4 h-4"
                  />
                  <div>
                    <span className="font-bold">Standard</span>
                    <span className="text-sm text-gray-600 ml-2">(No extra cost)</span>
                  </div>
                </label>
                <label className={`flex items-center gap-2 p-4 border-2 rounded-lg cursor-pointer transition-all ${selectedUrgency === 'express' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-400'}`}>
                  <input 
                    type="radio" 
                    name="urgency" 
                    value="express" 
                    checked={selectedUrgency === 'express'}
                    onChange={(e) => setSelectedUrgency(e.target.value)}
                    className="w-4 h-4"
                  />
                  <div>
                    <span className="font-bold text-orange-600">Express</span>
                    <span className="text-sm text-gray-600 ml-2">(Submission within 7 business days)</span>
                  </div>
                </label>
              </div>
            </div>

            {/* Additional Information */}
            <div className="space-y-4 mb-8 text-sm sm:text-base text-gray-800 leading-relaxed">
              <h3 className="font-bold text-lg underline">ADDITIONAL OPTIONS & FEES <span className="text-gray-500 italic font-normal text-base">(OPTIONAL)</span></h3>
              
              <div className="additional-option-clause">
                <p className="font-semibold">1. EXPRESS SERVICE (Submission within 7 business days)</p>
                <p className="ml-4 text-gray-600">
                  Standard turnaround times depend on our current workload. If you require urgent attention, 
                  you may opt for our Express Service to prioritize your file. Your file will jump the queue and be submitted within 7 business days.
                </p>
              </div>
              
              <div className="additional-option-clause">
                <p className="font-semibold">2. DISPUTE & APPEAL FEE</p>
                <p className="ml-4 text-gray-600">
                  The above packages cover the submission and verification of the return. Should SARS unlawfully 
                  disallow a valid deduction requiring a formal Notice of Objection (NOO) or Notice of Appeal (NOA), 
                  a separate Dispute Fee will be quoted based on the complexity of the legal argument required. The minimum dispute submission fee is {formatCurrencyDisplay(agreementFees?.disputeAppealFee)} per dispute/appeal.
                  This service is only rendered if all previous fees have been settled.
                </p>
              </div>

              <div className="additional-option-clause">
                <p className="font-semibold">3. TAX CONSULTATIONS</p>
                <p className="ml-4 text-gray-600">
                  We value open communication and are always eager to assist you with any tax-related queries. 
                  Each package includes a complimentary telephonic consultation: Bronze, Silver, and Gold packages 
                  include one (1) consultation of up to 30 minutes, while the Platinum package includes two (2) consultations 
                  of up to 30 minutes each. These included consultations are designed to address your immediate questions regarding the tax submission process.
                </p>
                <p className="ml-4 text-gray-600 mt-2">
                  Should you require additional consultations, we are more than happy to 
                  accommodate your needs. Please note that each additional telephonic consultation carries an additional fee of{' '}
                  {formatCurrencyDisplay(agreementFees?.taxConsultationFee)} per session (up to 30 minutes).
                </p>
              </div>

              <div className="additional-option-clause">
                <p className="font-semibold">4. EFILING PROFILE ACCESS & SECURITY</p>
                <p className="ml-4 text-gray-600">
                  Our standard packages assume your SARS eFiling profile is active and accessible. Should we need to intervene to unlock your profile or update your security details (such as email or phone number verification), this falls outside the scope of our standard submission services. A once-off fee of {formatCurrencyDisplay(agreementFees?.efilingActivationFee)} will be charged for this administrative assistance. To avoid delays, this amount will be invoiced separately and is payable upfront before we can schedule the necessary security authentication appointments with SARS.
                </p>
              </div>
            </div>

            {/* Client Signature Section */}
            <div className="border-t pt-6 mt-6">
              <h3 className="font-bold text-lg underline mb-4">CLIENT SELECTION</h3>
              
              {selectedPackage && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800">
                    <strong>Selected Package:</strong> {packages.find(p => p.id === selectedPackage)?.name} - {packages.find(p => p.id === selectedPackage)?.subtitle}
                  </p>
                  <p className="text-green-800">
                    <strong>Urgency Level:</strong> {selectedUrgency === 'express' ? 'Express (Submission within 7 business days)' : 'Standard'}
                  </p>
                  <p className="text-green-800 font-bold">
                    <strong>Total Min. Spend:</strong> {calculateTotalMinSpend()}
                    {packages.find(p => p.id === selectedPackage)?.refundFee !== '0%' && (
                      <span className="font-normal"> + {packages.find(p => p.id === selectedPackage)?.refundFee} of refund</span>
                    )}
                  </p>
                </div>
              )}
              
              <h3 className="font-bold text-lg underline mb-4">Accepted by client by virtue of signature</h3>
              
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Client name:</span>
                  <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">{fullName}</span>
                </div>

                {/* Client Signature */}
                <div className="signature-wrapper-pdf">
                  <p className="font-semibold mb-3">Client signature:</p>
                  <SignatureCanvas 
                    ref={signatureCanvasRef}
                    onSignatureChange={handleSignatureChange}
                    onClear={handleClearSignature}
                    heightClass="h-40 sm:h-48"
                    initialSignature={signature}
                  />
                </div>

                <br />

                {/* Signed at Section */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p>
                    Signed at <input 
                      type="text" 
                      value={signedAt}
                      onChange={handleLocationChange}
                      onBlur={(e) => setSignedAt(e.target.value.trim())}
                      placeholder="Enter location"
                      data-testid="page5-signed-at-input"
                      className="border-b-2 border-tca-blue bg-blue-50 px-2 py-1 w-40 focus:outline-none font-bold text-tca-blue rounded"
                    /> on this <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">{dayOfMonth}</span> day of <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">{monthName} {fullYear}</span>
                  </p>
                  {locationError && <p className="text-sm text-red-600 mt-2">{locationError}</p>}
                </div>
              </div>
            </div>

            <br />
            <br />

            {/* Action Buttons - Hidden in PDF */}
            <div className="flex flex-col sm:flex-row gap-3 items-center mt-8 pt-6 border-t" data-hide-in-pdf="true">
              <a 
                href="https://tax.tcauditors.co.za"
                className="inline-flex items-center justify-center text-xs font-bold px-4 py-2 h-9 rounded-md bg-slate-600 hover:bg-slate-700 text-white transition-all duration-200 shadow-sm"
                data-hide-in-pdf="true"
              >
                Start Over
              </a>
              <Button 
                type="button"
                variant="outline"
                onClick={handleBack}
                className="sm:w-auto"
                data-testid="page5-back-button"
              >
                ← Back to Page 4
              </Button>
              
              <Button 
                type="button"
                onClick={handleSubmit}
                disabled={!isFormValid() || isGeneratingPDF}
                data-testid="page5-next-button"
                className="sm:w-auto bg-accent hover:bg-accent/90 text-accent-foreground font-semibold py-3 px-8 h-12 text-base rounded-lg shadow-soft hover:shadow-card transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGeneratingPDF ? 'Saving...' : 'Go to Next Page...'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Page Indicator with TCA Logo */}
      <div className="relative">
        <PageIndicator currentPage={5} totalPages={7} pageTitle="Tax Return Optimisation & Pricing Guide" />
        <img 
          src={TCA_SHORT_LOGO} 
          alt="TCA Logo" 
          className="absolute bottom-2 right-56 h-12 w-auto object-contain opacity-80"
        />
      </div>

      {/* Location Discrepancy Warning Modal */}
      <AlertDialog open={showLocationWarning} onOpenChange={setShowLocationWarning}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-amber-600 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Location Discrepancy
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-sm">
                <p>
                  The location you entered on this page does not match the location on the previous page. 
                  Please ensure consistency across all pages.
                </p>
                <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Tax Services Master Engagement Letter:</span>
                    <span className="font-bold text-tca-blue">{clientData.signedAtPage4 || 'Not entered'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Tax Return Optimisation & Pricing Guide:</span>
                    <span className="font-bold text-tca-blue">{signedAt}</span>
                  </div>
                </div>
                <p className="text-gray-600">
                  Please go back and correct the location on either the <strong>Tax Services Master Engagement Letter</strong> or the <strong>Tax Return Optimisation & Pricing Guide</strong> to be consistent.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowLocationWarning(false)}>
              OK, I'll correct it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Express Upsell Modal */}
      <AlertDialog open={showExpressUpsellModal} onOpenChange={setShowExpressUpsellModal}>
        <AlertDialogContent className="max-w-lg" data-testid="express-upsell-modal">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-orange-600 flex items-center gap-2 text-xl">
              <Crown className="h-6 w-6" />
              Hold On - Don't Miss Out on VIP Treatment!
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-sm sm:text-base text-gray-700">
                <p>
                  You're about to proceed with our <strong>Standard</strong> service. Before you do, here's what
                  our <strong className="text-orange-600">Express</strong> clients get that you'd be leaving on the table:
                </p>

                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-orange-50 border border-orange-200">
                    <Crown className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <p><strong>VIP Priority Status:</strong> Express clients jump straight onto our VIP submission list - no waiting in the general queue.</p>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-orange-50 border border-orange-200">
                    <Clock className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <p><strong>7 Days vs. 30 Days:</strong> Express submissions are typically finalised within 7 business days. On Standard, it can take up to 30 days.</p>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-orange-50 border border-orange-200">
                    <MessageCircleMore className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <p><strong>Daily WhatsApp Updates:</strong> Express clients can follow up with us daily and stay in the loop the whole way. On Standard, submissions run strictly first-come-first-served, so follow-ups can only be entertained from day 31 onwards.</p>
                  </div>
                </div>

                <p className="text-gray-600 italic">
                  For a small once-off fee, you skip the queue entirely and stay informed every step of the way. Most clients tell us it was worth every cent.
                </p>

                <div className="text-center border-t pt-4">
                  <p className="text-red-600 font-bold" data-testid="express-spots-warning">
                    Only 4 Express spots left for the next 7 days - these normally fill up within 4 minutes!
                  </p>
                  <p className="text-red-600 text-2xl font-mono font-bold mt-1" data-testid="express-countdown-timer">
                    {Math.floor(expressSpotsCountdown / 60)}:{String(expressSpotsCountdown % 60).padStart(2, '0')}
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleStayStandard}
              data-testid="stay-standard-button"
              className="sm:w-auto"
            >
              Stay with Standard
            </Button>
            <Button
              type="button"
              onClick={handleUpgradeToExpress}
              data-testid="upgrade-to-express-button"
              className="sm:w-auto bg-orange-500 hover:bg-orange-600 text-white font-semibold"
            >
              Upgrade to Express
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PageFive;
